import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Receipt, Pencil, X, Check, Download, ArrowUpDown } from "lucide-react";
import { Tables } from "@/integrations/supabase/types";
import { ReceiptUpload } from "./ReceiptUpload";
import { logAudit, computeChanges } from "@/utils/auditLog";
import { useAuth } from "@/hooks/useAuth";

type VehicleExpense = Tables<"vehicle_expenses">;

const VEHICLE_EXPENSE_CATEGORIES = [
  { value: "Auction Fee", hstDefault: true },
  { value: "Transport/Towing", hstDefault: true },
  { value: "Mechanical Repairs", hstDefault: true },
  { value: "Body Work", hstDefault: true },
  { value: "Parts", hstDefault: true },
  { value: "Detailing", hstDefault: true },
  { value: "Fuel", hstDefault: true },
  { value: "Safety/Certification", hstDefault: true },
  { value: "CARFAX", hstDefault: true },
  { value: "Registration/Licensing", hstDefault: false },
  { value: "Insurance", hstDefault: false },
  { value: "Other", hstDefault: true },
];

interface VehicleExpensesProps {
  vehicleId: string;
  vehicleName: string; // "2019 Honda Civic"
  onExpensesChanged?: () => void;
}

interface ExpenseFormData {
  category: string;
  description: string;
  amount: string;
  hst_applicable: boolean;
  hst_amount: string;
  expense_date: string;
  receipt_url: string | null;
  receipt_filename: string | null;
}

const emptyForm: ExpenseFormData = {
  category: "",
  description: "",
  amount: "",
  hst_applicable: true,
  hst_amount: "",
  expense_date: new Date().toISOString().split("T")[0],
  receipt_url: null,
  receipt_filename: null,
};

export function VehicleExpenses({ vehicleId, vehicleName, onExpensesChanged }: VehicleExpensesProps) {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState<VehicleExpense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const draftKey = `vehicle_expense_draft_${vehicleId}`;

  const loadDraft = () => {
    try {
      const saved = localStorage.getItem(draftKey);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {}
    return { isAdding: false, formData: { ...emptyForm } };
  };

  const initialDraft = loadDraft();
  
  const [isAdding, setIsAdding] = useState(initialDraft.isAdding);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<ExpenseFormData>(initialDraft.formData);
  const [sortField, setSortField] = useState<"date" | "category" | "amount">("date");
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");

  // Save to localStorage whenever form data or isAdding changes
  useEffect(() => {
    if (!editingId) { // Only save drafts for new expenses, not edits
      localStorage.setItem(draftKey, JSON.stringify({ isAdding, formData }));
    }
  }, [isAdding, formData, editingId, draftKey]);

  const folderPath = `vehicles/${vehicleName.replace(/\s+/g, "-")}_${vehicleId.slice(0, 8)}`;

  useEffect(() => {
    loadExpenses();
  }, [vehicleId]);

  const loadExpenses = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("vehicle_expenses")
      .select("*")
      .eq("vehicle_id", vehicleId)
      .is("deleted_at", null)
      .order("expense_date", { ascending: false });

    if (error) {
      toast.error("Failed to load expenses");
    } else {
      setExpenses(data || []);
    }
    setIsLoading(false);
  };

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const sortedExpenses = [...expenses].sort((a, b) => {
    let aVal: any;
    let bVal: any;

    if (sortField === "date") {
      aVal = new Date(a.expense_date).getTime();
      bVal = new Date(b.expense_date).getTime();
    } else if (sortField === "category") {
      aVal = a.category.toLowerCase();
      bVal = b.category.toLowerCase();
    } else { // amount
      aVal = Number(a.amount) || 0;
      bVal = Number(b.amount) || 0;
    }

    if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
    if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const handleAmountChange = (value: string) => {
    setFormData((prev) => {
      const amount = parseFloat(value) || 0;
      const hst = prev.hst_applicable ? parseFloat((amount * 0.13).toFixed(2)) : 0;
      return { ...prev, amount: value, hst_amount: hst.toString() };
    });
  };

  const handleHstToggle = (checked: boolean) => {
    setFormData((prev) => {
      const amount = parseFloat(prev.amount) || 0;
      const hst = checked ? parseFloat((amount * 0.13).toFixed(2)) : 0;
      return { ...prev, hst_applicable: checked, hst_amount: hst.toString() };
    });
  };

  const handleCategoryChange = (value: string) => {
    const cat = VEHICLE_EXPENSE_CATEGORIES.find((c) => c.value === value);
    const hstApplicable = cat?.hstDefault ?? true;
    const amount = parseFloat(formData.amount) || 0;
    const hst = hstApplicable ? parseFloat((amount * 0.13).toFixed(2)) : 0;
    setFormData((prev) => ({
      ...prev,
      category: value,
      hst_applicable: hstApplicable,
      hst_amount: hst.toString(),
    }));
  };

  const resetForm = () => {
    setFormData({ ...emptyForm });
    setIsAdding(false);
    setEditingId(null);
    localStorage.removeItem(draftKey);
  };

  const startEdit = (expense: VehicleExpense) => {
    setEditingId(expense.id);
    setIsAdding(false);
    setFormData({
      category: expense.category,
      description: expense.description || "",
      amount: expense.amount.toString(),
      hst_applicable: expense.hst_applicable,
      hst_amount: expense.hst_amount.toString(),
      expense_date: expense.expense_date,
      receipt_url: expense.receipt_url,
      receipt_filename: expense.receipt_filename,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.category || !formData.amount) return;

    const amountVal = parseFloat(formData.amount);
    const hstAmountVal = parseFloat(formData.hst_amount) || 0;
    if (amountVal < 0 || hstAmountVal < 0) {
      toast.error("Expense amounts cannot be negative");
      return;
    }

    setIsSaving(true);

    const payload = {
      category: formData.category,
      description: formData.description || null,
      amount: amountVal,
      hst_amount: hstAmountVal,
      hst_applicable: formData.hst_applicable,
      expense_date: formData.expense_date,
      receipt_url: formData.receipt_url,
      receipt_filename: formData.receipt_filename,
    };

    if (editingId) {
      // UPDATE
      const originalExpense = expenses.find(e => e.id === editingId);
      const { error } = await supabase
        .from("vehicle_expenses")
        .update(payload)
        .eq("id", editingId);

      if (error) {
        toast.error("Failed to update expense");
      } else {
        toast.success("Expense updated");
        if (originalExpense) {
          const changes = computeChanges(
            originalExpense as unknown as Record<string, unknown>,
            payload as unknown as Record<string, unknown>,
            ['category', 'description', 'amount', 'hst_amount', 'hst_applicable', 'expense_date']
          );
          await logAudit({
            entityType: 'expense',
            entityId: editingId,
            action: 'updated',
            changes: Object.keys(changes).length > 0 ? changes : undefined,
            metadata: { category: payload.category, amount: payload.amount, expense_type: 'vehicle', vehicle_id: vehicleId, vehicle_name: vehicleName },
            performedBy: user?.id,
          });
        }
        resetForm();
        loadExpenses();
        onExpensesChanged?.();
      }
    } else {
      // INSERT
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const { data, error } = await supabase.from("vehicle_expenses").insert({
        ...payload,
        vehicle_id: vehicleId,
        created_by: authUser?.id || null,
      }).select().single();

      if (error) {
        toast.error("Failed to add expense");
      } else {
        toast.success("Expense added");
        if (data) {
          await logAudit({
            entityType: 'expense',
            entityId: data.id,
            action: 'created',
            metadata: { category: payload.category, amount: payload.amount, expense_type: 'vehicle', vehicle_id: vehicleId, vehicle_name: vehicleName },
            performedBy: authUser?.id,
          });
        }
        resetForm();
        loadExpenses();
        onExpensesChanged?.();
      }
    }
    setIsSaving(false);
  };

  const handleDelete = async (expense: VehicleExpense) => {
    if (!confirm(`Delete "${expense.category}" expense of $${expense.amount}?`)) return;

    const { error } = await supabase
      .from("vehicle_expenses")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", expense.id);

    if (error) {
      toast.error("Failed to delete expense");
    } else {
      toast.success("Expense deleted");
      await logAudit({
        entityType: 'expense',
        entityId: expense.id,
        action: 'deleted',
        metadata: { category: expense.category, amount: Number(expense.amount), expense_type: 'vehicle', vehicle_id: vehicleId, vehicle_name: vehicleName },
        performedBy: user?.id,
      });
      loadExpenses();
      onExpensesChanged?.();
    }
  };

  const totalExpenses = expenses.reduce((sum, e) => sum + Number(e.amount), 0);
  const totalHst = expenses.reduce((sum, e) => sum + Number(e.hst_amount), 0);

  const showForm = isAdding || editingId;

  const handleExportExcel = async () => {
    try {
      const ExcelJS = await import("exceljs");
      const { saveAs } = await import("file-saver");
      
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Expenses");

      worksheet.columns = [
        { header: "Date", key: "date", width: 15 },
        { header: "Category", key: "category", width: 25 },
        { header: "Description", key: "description", width: 40 },
        { header: "Amount (excl. HST)", key: "amount", width: 20 },
        { header: "HST", key: "hst", width: 15 },
        { header: "Total", key: "total", width: 15 },
      ];

      expenses.forEach((expense) => {
        worksheet.addRow({
          date: new Date(expense.expense_date + "T00:00:00").toLocaleDateString("en-CA"),
          category: expense.category,
          description: expense.description || "",
          amount: Number(expense.amount),
          hst: Number(expense.hst_amount),
          total: Number(expense.amount) + Number(expense.hst_amount),
        });
      });

      worksheet.addRow({});
      worksheet.addRow({
        date: "Total",
        amount: totalExpenses,
        hst: totalHst,
        total: totalExpenses + totalHst,
      });

      // Style header
      worksheet.getRow(1).font = { bold: true };
      
      // Format currency columns
      ["D", "E", "F"].forEach((col) => {
        worksheet.getColumn(col).numFmt = '"$"#,##0.00';
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, `${vehicleName.replace(/\s+/g, "_")}_Expenses.xlsx`);
      toast.success("Expenses exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export expenses");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            Vehicle Expenses
          </h3>
          <p className="text-xs text-muted-foreground">
            Total: ${totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2 })} + ${totalHst.toLocaleString(undefined, { minimumFractionDigits: 2 })} HST
          </p>
        </div>
        {!showForm && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleExportExcel} disabled={expenses.length === 0}>
              <Download className="h-4 w-4 mr-1" /> Export
            </Button>
            <Button size="sm" onClick={() => { setIsAdding(true); setEditingId(null); setFormData({ ...emptyForm }); }}>
              <Plus className="h-4 w-4 mr-1" /> Add Expense
            </Button>
          </div>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="border rounded-lg p-4 space-y-3 bg-muted/30">
          <p className="font-semibold text-sm">{editingId ? "Edit Expense" : "Add Expense"}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Category *</Label>
              <Select value={formData.category} onValueChange={handleCategoryChange}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {VEHICLE_EXPENSE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.value}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={formData.expense_date}
                onChange={(e) => setFormData((prev) => ({ ...prev, expense_date: e.target.value }))}
                required
              />
            </div>
            <div>
              <Label>Amount (before HST) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={formData.amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                required
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>HST Amount</Label>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">HST applies</Label>
                  <Switch
                    checked={formData.hst_applicable}
                    onCheckedChange={handleHstToggle}
                  />
                </div>
              </div>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={formData.hst_amount}
                onChange={(e) => setFormData((prev) => ({ ...prev, hst_amount: e.target.value }))}
                disabled={!formData.hst_applicable}
              />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Optional notes about this expense"
              rows={2}
            />
          </div>
          <div>
            <Label>Receipt</Label>
            <ReceiptUpload
              folderPath={folderPath}
              category={formData.category || "Expense"}
              contextLabel={vehicleName}
              onUploaded={(url, filename) =>
                setFormData((prev) => ({ ...prev, receipt_url: url, receipt_filename: filename }))
              }
              existingUrl={formData.receipt_url}
              existingFilename={formData.receipt_filename}
              onRemoved={() =>
                setFormData((prev) => ({ ...prev, receipt_url: null, receipt_filename: null }))
              }
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
            <Button type="submit" disabled={isSaving || !formData.category || !formData.amount}>
              {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {editingId ? "Update Expense" : "Save Expense"}
            </Button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="text-center py-6 text-muted-foreground text-sm">Loading expenses...</div>
      ) : expenses.length === 0 ? (
        <div className="text-center py-6 text-muted-foreground text-sm border rounded-lg">
          No expenses recorded yet
        </div>
      ) : (
        <div>
          {/* Mobile: Card layout */}
          <div className="sm:hidden space-y-3">
            {sortedExpenses.map((expense) => (
              <div key={expense.id} className={`border rounded-lg p-3 bg-card space-y-2 ${editingId === expense.id ? "ring-2 ring-primary" : ""}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">{expense.category}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(expense.expense_date + "T00:00:00").toLocaleDateString("en-CA")}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">${Number(expense.amount).toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">+${Number(expense.hst_amount).toFixed(2)} HST</p>
                  </div>
                </div>
                {expense.description && (
                  <p className="text-xs text-muted-foreground">{expense.description}</p>
                )}
                <div className="flex items-center justify-between pt-1 border-t">
                  <div className="flex-1 min-w-0">
                    {expense.receipt_url ? (
                      <ReceiptUpload
                        folderPath={folderPath}
                        category={expense.category}
                        contextLabel={vehicleName}
                        onUploaded={() => {}}
                        existingUrl={expense.receipt_url}
                        existingFilename={expense.receipt_filename}
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground">No receipt</span>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0 ml-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10"
                      onClick={() => startEdit(expense)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 text-destructive hover:text-destructive"
                      onClick={() => handleDelete(expense)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Table layout */}
          <div className="hidden sm:block border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted select-none">
                  <tr>
                    <th onClick={() => toggleSort("date")} className="text-left p-3 font-medium cursor-pointer hover:bg-muted/80">
                      <span className="flex items-center gap-1">Date <ArrowUpDown className="h-3.5 w-3.5" /></span>
                    </th>
                    <th onClick={() => toggleSort("category")} className="text-left p-3 font-medium cursor-pointer hover:bg-muted/80">
                      <span className="flex items-center gap-1">Category <ArrowUpDown className="h-3.5 w-3.5" /></span>
                    </th>
                    <th className="text-left p-3 font-medium">Description</th>
                    <th onClick={() => toggleSort("amount")} className="text-right p-3 font-medium cursor-pointer hover:bg-muted/80">
                      <span className="flex items-center justify-end gap-1">Amount <ArrowUpDown className="h-3.5 w-3.5" /></span>
                    </th>
                    <th className="text-right p-3 font-medium">HST</th>
                    <th className="text-center p-3 font-medium">Receipt</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sortedExpenses.map((expense) => (
                    <tr key={expense.id} className={`hover:bg-muted/50 ${editingId === expense.id ? "bg-primary/5" : ""}`}>
                      <td className="p-3 whitespace-nowrap">
                        {new Date(expense.expense_date + "T00:00:00").toLocaleDateString("en-CA")}
                      </td>
                      <td className="p-3">{expense.category}</td>
                      <td className="p-3 text-muted-foreground truncate max-w-[200px]">
                        {expense.description || "—"}
                      </td>
                      <td className="p-3 text-right">${Number(expense.amount).toFixed(2)}</td>
                      <td className="p-3 text-right text-muted-foreground">
                        ${Number(expense.hst_amount).toFixed(2)}
                      </td>
                      <td className="p-3 text-center">
                        {expense.receipt_url ? (
                          <ReceiptUpload
                            folderPath={folderPath}
                            category={expense.category}
                            contextLabel={vehicleName}
                            onUploaded={() => {}}
                            existingUrl={expense.receipt_url}
                            existingFilename={expense.receipt_filename}
                          />
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => startEdit(expense)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => handleDelete(expense)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-muted/50 border-t-2">
                  <tr className="font-semibold">
                    <td className="p-3" colSpan={3}>Total</td>
                    <td className="p-3 text-right">${totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="p-3 text-right">${totalHst.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="p-3" colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
